<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ConfigProvider :locale="zhCN">
    <div class="big-box" ref="bigBox">
      <Card>
        <Popover  placement="rightTop" >
          <template #content>
            <div style="font-size:13px;width:700px" v-html="CONTENT"></div>
          </template>
          <span style="display:inline-flex;margin-bottom:10px">如何使用数据下载服务<QuestionCircleOutlined  style="margin-left: 3px"/></span>
        </Popover>
        <Form label-align="right" layout="inline" :model="formState">

          <FormItem label="文件名称">
            <Input v-model:value="formState.name" allow-clear placeholder="请输入" />
          </FormItem>

          <FormItem :colon="false" label="创建人">
            <SelectOptimizer v-model:value="formState.createUser" :is-multiple="false" />
          </FormItem>

          <Button type="primary" @click="onSearch">
            查询
          </Button>
        </Form>
      </Card>

      <a-card style="margin-top: 10px">
        <BaseTable :columns="columns" :is-loading="isLoading" :pagination="pagination" :table-data="tableData"
          @change-page="onChangePage">
          <template #bodyCell="{ column, record }">
            <template v-if="column.dataIndex === 'name'">
              <span>{{ record.name }}</span>
            </template>

            <template v-if="column.dataIndex === 'dataType'">
              <span v-if="record.dataType === 0" style="color: #67C23A">一般数据</span>
              <span v-else style="color: #F56C6C">敏感数据</span>
            </template>

            <template v-if="column.dataIndex === 'status'">
              <span v-if="record.status !== DATA_STATUS.FAILURE">{{ DATA_STATUS_MAPPING[record.status] }}</span>

              <Tooltip v-else>
                <template #title>
                  <span>{{ record.remark }}</span>
                </template>
                <span>{{ DATA_STATUS_MAPPING[record.status] }}</span>
              </Tooltip>
            </template>

            <template v-if="column.dataIndex === 'approvalStatus'">
              <span v-if="record.status === 1">
                <span v-if="!record.approval">
                  无审批
                </span>
                <span v-else>{{ approvalStatusMap[record.approvalStatus] }}</span>
              </span>
              <span v-else>/</span>
            </template>

            <template v-if="column.dataIndex === 'action'">
              <Button class="clear-padding"  v-if="record.status === 1 && (record.approvalStatus === approvalStatusWords.APPROVED || !record.approval) && btnAuth.downLoad" :disabled="record.status !== DATA_STATUS.DOWNLOAD" type="link"
                @click="downloadItem(record)">
                下载
              </Button>
              <Button class="clear-padding "  v-if="record.status === 1 && (record.approvalStatus === approvalStatusWords.APPROVED || !record.approval) && btnAuth.log" :disabled="record.status !== DATA_STATUS.DOWNLOAD" type="link"
                @click="showLogDialog(record)">
                日志
              </Button>
              <Button class="clear-padding"  v-if="record.approvalStatus !== approvalStatusWords.PENDING && btnAuth.delete" danger type="link" @click="deleteItem(record.id)">
                删除
              </Button>
            </template>
          </template>
        </BaseTable>
      </a-card>

      <LogDialog v-if="logState.visible" :id="logState.id" v-model:visible="logState.visible" />
    </div>
  </ConfigProvider>
</template>

<script setup lang="ts">
import { type Pagination, type DataDownLoadListItem, type FormState } from './types'
import { type UnwrapRef, onMounted, reactive, ref, defineProps } from 'vue'
import { dataDownload, deleteDataDownLoadList, getApprovalStatusOption, getDataDownLoadList } from './service'
import BaseTable from './components/base-table/index.vue'
import SelectOptimizer from './components/select-optimizer/index.vue'
import { Modal, type TableColumnsType, message } from 'ant-design-vue'
import dayjs from 'dayjs'
import { DATA_STATUS, DATA_STATUS_MAPPING, CONTENT } from './constants'
import { saveAs } from 'file-saver'
import LogDialog from './components/log-dialog.vue'
import { Card, Form, FormItem, Input, Button, Tooltip, ConfigProvider, Popover } from 'ant-design-vue'
import zhCN from 'ant-design-vue/es/locale/zh_CN';
import { QuestionCircleOutlined } from '@ant-design/icons-vue'

interface Props{
  btnAuthGroup:string
}

const props = defineProps<Props>()
const btnAuth = JSON.parse(props.btnAuthGroup)
const permissionLevel = ref<number>(0)
const isLoading = ref<boolean>(false)
const tableData = ref<DataDownLoadListItem[]>()
const pagination = reactive<Pagination>({
  pageSize: 20,
  page: 1,
  total: 0,
})
const logState = reactive({
  id: 20,
  visible: false,
})
const formState: UnwrapRef<FormState> = reactive({
  name: '',
  createUser: null,
})
const bigBox = ref<HTMLDivElement>();
const approvalStatusMap = ref<Record<string, string>>({})
const approvalStatusWords = {
  PENDING: 'PENDING', // 审批中
  APPROVED: 'APPROVED', // 审批通过
  CANCELED: 'CANCELED', // 已撤回
  REJECTED: 'REJECTED', // 审批拒绝
}

getApprovalStatusOption().then(res => {
  res.forEach(item => {
    approvalStatusMap.value[item.value] = item.text
  })
})

const showLogDialog = (item: DataDownLoadListItem) => {
  Object.assign(logState, {
    id: item.id,
    visible: true,
  })
}

const downloadItem = (item: DataDownLoadListItem) => {
  dataDownload(item.id, item.ossUrl)
    .then(res => {
      saveAs(res.fileUrl)
    })
}

const deleteItem = (id: number) => {
  Modal.confirm({
    title: '确认',
    content: '确定删除吗？',
    okText: '确定',
    cancelText: '取消',
    closable: true,
    onOk: () => {
      deleteDataDownLoadList(id)
        .then(() => {
          getList()
          message.success('删除成功')
        })
    },
  })
}

const getList = async () => {
  isLoading.value = true

  return getDataDownLoadList({
    pageNum: pagination.page,
    pageSize: pagination.pageSize,
    queryCreateUser: formState.createUser,
    queryName: formState.name,
  })
    .then((res) => {
      pagination.total = +res.page.total
      permissionLevel.value = res.permissionLevel

      const list = res.page.list || []

      list.forEach(item => {
        item.createTimeStr = dayjs(+item.createTime * 1000).format('YYYY-MM-DD HH:mm:ss')
      })
      tableData.value = list
    })
    .finally(() => {
      isLoading.value = false
    })
}

const onChangePage = (page: number) => {
  pagination.page = page
  getList()
}

const onSearch = () => {
  pagination.page = 1
  getList()
}

onMounted(() => {
  getList()

  document.querySelectorAll("style").forEach((item) => {
    bigBox.value?.appendChild(item.cloneNode(true));
  })
})

const columns = ref<TableColumnsType>([
  {
    title: '文件名称',
    dataIndex: 'name',
    resizable: true,
    width: 150,
  },
  {
    title: '数据量',
    dataIndex: 'fileNum',
    resizable: true,
    width: 100,
  },
  {
    title: '文件类型',
    dataIndex: 'dataType',
    resizable: true,
    width: 100,
  },
  {
    title: '文件大小',
    dataIndex: 'fileSizeStr',
    resizable: true,
    width: 100,
  },
  {
    title: '创建者',
    dataIndex: 'createUser',
    resizable: true,
    width: 100,
  },
  {
    title: '创建时间',
    dataIndex: 'createTimeStr',
    resizable: true,
    width: 100,
  },
  {
    title: '文件生成状态',
    dataIndex: 'status',
    resizable: true,
    width: 100,
  },
  {
    title: '流程审批状态',
    dataIndex: 'approvalStatus',
    resizable: true,
    width: 100,
  },
  {
    title: '高级操作',
    dataIndex: 'action',
    resizable: true,
    width: 140,
  },
])
</script>

<style scoped lang="less">
.big-box {
  .ant-card {
    margin-bottom: 20px;
  }

  .clear-padding {
    padding: 0 10px 0 0;
  }
}

.alert {
  margin-left: 80px;
  margin-right: 55px;
  margin-top: 10px;
}

.copyBtn {
  color: #1890ff;
  cursor: pointer;
  font-size: 12px;
  height: 100%;
}

p {
  font-size: 14px;
  color: #333;
  margin-bottom: 0px;
  margin-top: 0px;
}

.textarea {
  width: 400px;
  height: 120px;
  border: 1px solid #d9d9d9;
  padding: 10px;
  border-radius: 6px;
  overflow-y: auto;
  position: relative;
  overflow-x: hidden;
  word-break: break-all;
  padding-bottom: 24px;
}

.flex {
  display: flex;
}

.mask {
  height: 100%;
  width: 100%;
  position: fixed;
  z-index: 4999;
  background-color: #33333390;
  display: flex;
  align-items: center;
  justify-content: center;
  left: 0;
  top: 0;
}

.container {
  background-color: white;
  position: relative;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 20px;
  border-radius: 10px;
  margin-top: -5%;
  height: 70%;
  overflow-y: auto;
}

.redText {
  color: red;
  font-size: 12px;
}

.link {
  color: #1890ff;
  cursor: pointer;
  font-size: 14px;
  margin-left: 8px;
}

.mb-10 {
  margin-bottom: 10px;
}

.ml-10 {
  margin-left: 10px;
}

.mr-10 {
  margin-right: 10px;
}

.w-200 {
  width: 200px;
}

.w-400 {
  width: 400px;
}

.boxItem {
  display: flex;
  align-items: baseline;
  margin: 20px 0;
}

.boxTitle {
  width: 100px;
}

.lineBox {
  display: flex;
  align-items: center;
  margin: 10px;
}

.none {
  display: none;
}

.ant-btn {
  font-size: 14px;
  height: 32px;
  padding: 4px 15px;
  border-radius: 6px;
}

 .ant-btn {
  outline: none;
  position: relative;
  display: inline-block;
  font-weight: 400;
  white-space: nowrap;
  text-align: center;
  background-image: none;
  background-color: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  touch-action: manipulation;
  line-height: 1.5714285714285714;
  color: rgba(0, 0, 0, 0.88);
}

.ant-btn-primary {
  color: #fff;
  background-color: #1677ff;
  box-shadow: 0 2px 0 rgba(5, 145, 255, 0.1);
}

.ant-btn-default {
  background-color: #ffffff;
  border-color: #d9d9d9;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.02);
}

.ant-btn-link{
  color: #1677ff
}

.ml-80 {
  margin-left: 80px;
}
</style>
